import { Button, DatePicker, Drawer, Form, Input } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceApp } from 'src/api/facade/types'

import { AppPermissionsSelect } from './AppPermissionsSelect'

export type CreateFormValues = {
  name: string
  expiresAt?: Dayjs | null
  permission_codes?: string[]
}

type Props = {
  open: boolean
  app: WorkspaceApp
  submitting: boolean
  onSubmit: (values: CreateFormValues) => void
  onClose: () => void
}

/**
 * Create-only drawer for app credentials — captures name, expiry, and the
 * permissions this token will grant (see `AppPermissionsSelect`).
 */
export const AppCredentialCreateDrawer = ({
  open,
  app,
  submitting,
  onSubmit,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<CreateFormValues>()

  useEffect(() => {
    if (!open) return
    form.resetFields()
  }, [open, form])

  const handleClose = () => {
    onClose()
    form.resetFields()
  }

  return (
    <Drawer
      title={t('workspace.apps.credentials.createDrawerTitle')}
      open={open}
      size={420}
      destroyOnHidden
      onClose={handleClose}
      extra={
        <Button
          type="primary"
          loading={submitting}
          onClick={() => form.submit()}
        >
          {t('workspace.apps.credentials.create')}
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onSubmit}
      >
        <Form.Item
          label={t('name')}
          name="name"
          rules={[{ required: true, message: t('required_field_error') }]}
        >
          <Input placeholder={t('placeholders.credentialName')} />
        </Form.Item>
        <Form.Item
          label={t('workspace.apps.credentials.expiresAt')}
          name="expiresAt"
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            placeholder={t('workspace.apps.credentials.expiresAtPlaceholder')}
            disabledDate={(current) => current && current.isBefore(dayjs())}
          />
        </Form.Item>
        <Form.Item
          label={t('workspace.apps.credentials.permissions')}
          name="permission_codes"
          extra={t('workspace.apps.credentials.permissionsHint')}
        >
          <AppPermissionsSelect app={app} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
